import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { TOPIC_LABELS, formatKeyBlocks, publicApi, savedKeys } from "@/lib/publicApi";
import { hasSecureStoreBridge, secretVault } from "@/lib/nativeBridge";
import { formatApiError } from "@/lib/api";
import { EmergencyBar } from "./SaudeLayout";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

export default function AnonymousQuestion() {
  const [topic, setTopic] = useState("tratamentos");
  const [question, setQuestion] = useState("");
  const [notEmergency, setNotEmergency] = useState(false);
  const [noPersonalData, setNoPersonalData] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [storeLocally, setStoreLocally] = useState(false);
  const [vaultSaved, setVaultSaved] = useState(false);
  const nativeVault = hasSecureStoreBridge();

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || result) return;
    setSubmitting(true);
    try {
      const { data } = await publicApi.post("/anonymous-questions", {
        topic,
        question,
        acknowledged_not_emergency: notEmergency,
        acknowledged_no_personal_data: noPersonalData,
      });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setSubmitting(false);
  };

  if (result) {
    return (
      <div data-testid="anonymous-question-success" className="space-y-6">
        <ShieldCheck size={24} className="text-sage-600" />
        <h1 className="font-display text-2xl font-medium tracking-tight text-ink-900">Pergunta enviada</h1>
        <p data-testid="result-notice" className="text-sm leading-relaxed text-ink-600">
          {result.notice}
        </p>

        <div className="er-card space-y-4">
          <div>
            <p className="er-label">Chave de acesso</p>
            <code data-testid="result-secret-key" className="mt-1 block break-all font-mono text-base tracking-[0.12em] text-ink-900">
              {formatKeyBlocks(result.secret_key)}
            </code>
            <p className="mt-2 text-xs text-ink-400">Os espaços são só para facilitar a leitura; ao copiar, a chave vai sem espaços.</p>
          </div>
          <Button
            data-testid="result-copy"
            onClick={() => {
              navigator.clipboard?.writeText(result.secret_key);
              toast.success("Chave copiada");
            }}
            className="h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700"
          >
            <Copy size={16} className="mr-2" /> Copiar chave
          </Button>
          <label className="flex items-start gap-3 text-sm text-ink-600">
            <Checkbox
              data-testid="result-store-consent"
              checked={storeLocally}
              onCheckedChange={(v) => {
                setStoreLocally(!!v);
                if (v) {
                  savedKeys.save(result.secret_key);
                  toast.success("Salvo apenas neste dispositivo");
                } else {
                  savedKeys.remove(result.secret_key);
                }
              }}
            />
            Salvar a chave neste dispositivo (opcional, fica só aqui).
          </label>
          {nativeVault ? (
            <Button
              data-testid="result-vault-save"
              variant="outline"
              disabled={vaultSaved}
              onClick={async () => {
                const res = await secretVault.save(result.secret_key);
                if (res?.ok) {
                  setVaultSaved(true);
                  toast.success("Chave guardada com biometria neste aparelho");
                } else {
                  toast.error("Não foi possível guardar a chave com biometria.");
                }
              }}
              className="h-11 w-full rounded-full"
            >
              {vaultSaved ? "Chave guardada com biometria" : "Guardar com biometria"}
            </Button>
          ) : (
            <p data-testid="vault-web-note" className="text-xs text-ink-400">
              No navegador a chave fica guardada apenas neste dispositivo, sem proteção por biometria. No aplicativo você
              pode protegê-la com digital ou reconhecimento facial.
            </p>
          )}
        </div>

        <Link
          to="/saude/fale-com-a-erimus/consultar"
          data-testid="go-to-lookup"
          className="flex min-h-[52px] items-center justify-center rounded-full border border-sage-600/40 bg-white text-sm font-medium text-sage-700"
        >
          Consultar resposta
        </Link>
        <EmergencyBar />
      </div>
    );
  }

  return (
    <div data-testid="anonymous-question-page" className="space-y-6">
      <Link to="/saude/fale-com-a-erimus" className="inline-flex items-center gap-2 text-sm text-ink-500" data-testid="question-back">
        <ArrowLeft size={16} /> Voltar
      </Link>

      <div>
        <p className="er-label">Pergunta sem identificação</p>
        <h1 className="mt-3 font-display text-2xl font-medium tracking-tight text-ink-900">Envie sua dúvida</h1>
        <p data-testid="anonymity-explanation" className="mt-3 rounded-2xl bg-sand/70 p-4 text-sm leading-relaxed text-ink-600">
          Não solicitamos nome, CPF, e-mail ou telefone. Evite escrever informações que possam identificar você ou outra
          pessoa.
        </p>
      </div>

      <form onSubmit={submit} className="space-y-5">
        <div>
          <Label htmlFor="topic" className="text-sm text-ink-600">
            Tema da pergunta
          </Label>
          <select
            id="topic"
            data-testid="question-topic-select"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="mt-2 h-12 w-full rounded-xl border border-border bg-white px-3 text-sm"
          >
            {Object.entries(TOPIC_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <Label htmlFor="question" className="text-sm text-ink-600">
            Sua pergunta
          </Label>
          <Textarea
            id="question"
            data-testid="question-text-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            rows={6}
            maxLength={4000}
            className="mt-2 rounded-xl bg-white"
            placeholder="Escreva sua dúvida sem incluir dados que identifiquem alguém."
            required
          />
          <p className="mt-1 text-xs text-ink-400">{question.length}/4000</p>
        </div>

        <label className="flex items-start gap-3 text-sm text-ink-600">
          <Checkbox data-testid="question-ack-emergency" checked={notEmergency} onCheckedChange={(v) => setNotEmergency(!!v)} />
          Entendi que este canal não atende emergências.
        </label>
        <label className="flex items-start gap-3 text-sm text-ink-600">
          <Checkbox data-testid="question-ack-personal" checked={noPersonalData} onCheckedChange={(v) => setNoPersonalData(!!v)} />
          Fui orientado a não inserir dados que identifiquem pessoas.
        </label>

        <Button
          type="submit"
          data-testid="question-submit"
          disabled={submitting || !notEmergency || !noPersonalData}
          className="h-12 w-full rounded-full bg-sage-600 text-bone hover:bg-sage-700"
        >
          {submitting ? "Enviando…" : "Enviar pergunta"}
        </Button>
      </form>
      <EmergencyBar />
    </div>
  );
}
